import { UPLOAD_FOLDER } from '../Constants/upload-folders.constant';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, shareReplay, throwError } from 'rxjs';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarRef,
  MatSnackBarVerticalPosition,
  TextOnlySnackBar,
} from '@angular/material/snack-bar';
import { environment } from '../../../../environments/environment';
import { request_urls, role_urls, common_urls } from '../Constants/api-constants';
import { PlayerStatsQuery, PlayerStatsResponse } from '../../../models/dashboard/player-stats.model';
import { PlayerVideosQuery } from '../../../models/dashboard/player-dashboard-v2.model';
import { pickPlayerStatsPayload } from '../../../models/dashboard/player-stats.util';
import { buildHttpParams } from '../point-leaderboard/point-leaderboard.service';
import { HttpRes } from '../../../models/common/http.model';
import { EVENT_TYPE_CODE } from '../../event-type/event-type.constants';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    
  }),
  observe: 'response' as 'response'
};

/** Per-call overrides for {@link DashboardService.toast}; omit for the shared defaults. */
export interface ToastOptions {
  /** Overrides the shared auto-dismiss duration (ms). */
  duration?: number;
  /** Extra panel classes applied alongside the `type` class. */
  panelClass?: string[];
  horizontalPosition?: MatSnackBarHorizontalPosition;
  verticalPosition?: MatSnackBarVerticalPosition;
  /**
   * Defaults to true: while the auth panel is on screen the toast centres on it
   * instead of the viewport. Pass false to keep it viewport-centred regardless.
   */
  anchor?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  /** `styleClass` of the auth sidebar in `app.component.html`. */
  private static readonly AUTH_PANEL_SELECTOR = '.auth_common_sidebar_modal';
  private static readonly ANCHOR_CLASS = 'st-toast-anchored';

  constructor(private readonly httpClient: HttpClient, private readonly snack_bar: MatSnackBar) { }

  durationInSeconds: number = 3000;
  filteredTabs: any;
  user_role_id: any = 1; // admin id
  role_id: any = 2; // selected role id
  roleDetail: any
  activeTab: number = 0;
  // both upper values are temprary
  /**
   * Shared toast. Returns the ref so callers can chain `afterDismissed()`;
   * the `close` action is already wired to dismiss.
   */
  toast(
    message: string,
    type: string,
    options?: ToastOptions
  ): MatSnackBarRef<TextOnlySnackBar> {
    const sb = this.snack_bar.open(message, 'close', {
      duration: options?.duration ?? this.durationInSeconds,
      panelClass: [
        type,
        ...(options?.panelClass ?? []),
        ...this.resolveAnchorClasses(options?.anchor ?? true),
      ],
      horizontalPosition: options?.horizontalPosition ?? 'center',
      verticalPosition: options?.verticalPosition ?? 'bottom',
    });
    sb.onAction().subscribe(() => {
      sb.dismiss();
    });
    return sb;
  }

  /**
   * The auth panel is a right-anchored PrimeNG sidebar that PrimeNG removes from
   * the DOM when closed, so its presence is the "is it on screen" test. Publishes
   * its geometry for the anchored-toast rule in `styles.scss` and returns the
   * anchor class; returns nothing when the panel is absent, leaving the toast
   * viewport-centred. Measured per toast, so it follows the panel across resizes
   * and breakpoints without a listener.
   */
  private resolveAnchorClasses(anchor: boolean): string[] {
    const doc = globalThis.document;
    if (!anchor || !doc) {
      return [];
    }

    const rect = doc
      .querySelector(DashboardService.AUTH_PANEL_SELECTOR)
      ?.getBoundingClientRect();
    if (!rect?.width) {
      return [];
    }

    const root = doc.documentElement;
    root.style.setProperty('--st-toast-anchor-x', `${rect.left + rect.width / 2}px`);
    root.style.setProperty('--st-toast-anchor-width', `${rect.width}px`);
    return [DashboardService.ANCHOR_CLASS];
  }
  fileUpload(formData: any, folder: string = UPLOAD_FOLDER.USER_PROFILE) {
    const reqHeader = new HttpHeaders({
      'enctype': 'multipart/form-data',
      'auth': 'Bearer ' + (globalThis.window?.localStorage ? localStorage.getItem('token') ?? '' : '')
    });
    return this.httpClient.post(environment.apiUrl + 'file_upload?path=' + encodeURIComponent(folder), formData,
      {
        headers: reqHeader,
        params: {},
        observe: 'response' as const,
      }
    ).pipe(
      map((response: any): HttpRes => {
        return response;
      }));
  }
  get_role_parents(id: number): Observable<any> {
    return this.httpClient.get<any>(role_urls.get_role_parents + '?role_id=' + id, httpOptions).pipe(
      shareReplay(),
      map((response: any): HttpRes => {
        return {
          code: response.status,
          msg: response.body.msg,
          data: response.body
        } as any;
      })
    );
  }
  createMember(data: any, user_role_id: any): Observable<HttpRes> {
    let params = new HttpParams();

    if (user_role_id > 0) {
      params = params.set('created_by', user_role_id);
    }
    return this.httpClient.post<any>(request_urls.create_user_role, data, { params, ...httpOptions }).pipe(
      shareReplay(),
      map((response: any): HttpRes => {
        return {
          code: response.status,
          msg: response.body.msg,
          data: response.body.data
        } as any;
      })
    );
  }

  /**
   * Consolidated dashboard landing endpoint.
   * Replaces the several per-widget calls (player info, top finishes,
   * recent matches, events, performance metrics) with a single request.
   * See GET /dashboard/v1/player/stats.
   */
  getPlayerStats(query: PlayerStatsQuery = {}): Observable<any> {
    const params = buildHttpParams({
      year: query.year,
      stats_type: query.stats_type,
      recent_matches_limit: query.recent_matches_limit,
      recent_tournaments_limit: query.recent_tournaments_limit,
      recent_leagues_limit: query.recent_leagues_limit,
      top_finish_limit: query.top_finish_limit,
      search: query.search,
      event_type: query.event_type,
    });

    return this.httpClient.get<any>(common_urls.get_player_stats, { params, ...httpOptions }).pipe(
      shareReplay(),
      map((response: any) => {
        const body = response.body ?? response;
        return {
          code: response.status ?? Number(body?.code),
          msg: body?.msg,
          data: pickPlayerStatsPayload(body?.data) ?? pickPlayerStatsPayload(body)
        } as { code?: number; msg?: string; data?: PlayerStatsResponse | null };
      })
    );
  }

  /**
   * Head-to-head comparison vs an opponent. Returns ALL aggregate + per-category
   * breakdown in a single call. See GET /dashboard/v1/player/head-to-head.
   */
  getPlayerHeadToHead(opponentId: number, categoryId?: number | null): Observable<any> {
    let params = new HttpParams()
      .set('opponent_id', opponentId.toString())
      .set('event_type', EVENT_TYPE_CODE.TOURNAMENT);
    if (categoryId != null) {
      params = params.set('category_id', categoryId.toString());
    }

    return this.httpClient.get<any>(common_urls.get_player_head_to_head, { params, ...httpOptions }).pipe(
      shareReplay(),
      map((response: any) => {
        return {
          code: response.status,
          msg: response.body?.msg,
          data: response.body?.data
        } as any;
      })
    );
  }

  /**
   * Year-scoped match performance only (lighter than the full consolidated
   * stats call). See GET /dashboard/v1/player/performance.
   * Response: { year, total_matches, matches_won, matches_lost, win_percentage }.
   */
  getPlayerPerformance(year: number): Observable<any> {
    const params = new HttpParams().set('year', year.toString());

    return this.httpClient.get<any>(common_urls.get_player_performance, { params, ...httpOptions }).pipe(
      shareReplay(),
      map((response: any) => {
        return {
          code: response.status,
          msg: response.body?.msg,
          data: response.body?.data
        } as any;
      })
    );
  }

  getPlayerGraphs(
    graphType: 'ranking' | 'rating' | 'elo',
    timeRange: string,
    metricType: 'rank' | 'points',
    categoryId?: number | null,
    options?: { seasonId?: number | null; includeLedger?: boolean }
  ): Observable<any> {
    let params = new HttpParams()
      .set('graph_type', graphType)
      .set('metric_type', metricType)
      .set('time_range', timeRange);

    if (categoryId != null && Number.isFinite(Number(categoryId))) {
      params = params.set('category_id', String(Number(categoryId)));
    }
    if (options?.seasonId != null) {
      params = params.set('season_id', options.seasonId.toString());
    }
    if (options?.includeLedger) {
      params = params.set('include_ledger', 'true');
    }

    return this.httpClient.get<any>(common_urls.get_player_graphs, { params, ...httpOptions }).pipe(
      shareReplay(),
      map((response: any): HttpRes => {
        return {
          code: response.status,
          msg: response.body.msg,
          data: response.body.data
        } as any;
      })
    );
  }

  getPlayerLive(categoryId?: number | null): Observable<any> {
    const params = buildHttpParams({
      category_id: categoryId == null ? undefined : String(categoryId)
    });
    return this.unwrap(common_urls.get_player_live, params);
  }

  getPlayerActivity(year?: number): Observable<any> {
    const params = buildHttpParams({ year });
    return this.unwrap(common_urls.get_player_activity, params);
  }

  getPlayerInsights(eventType: string = EVENT_TYPE_CODE.TOURNAMENT): Observable<any> {
    const params = buildHttpParams({ event_type: eventType });
    return this.unwrap(common_urls.get_player_insights, params);
  }

  getPlayerLeague(leagueEventId?: number | null): Observable<any> {
    const params = buildHttpParams({ league_event_id: leagueEventId });
    return this.unwrap(common_urls.get_player_league, params);
  }

  getPlayerCareer(): Observable<any> {
    return this.unwrap(common_urls.get_player_career);
  }

  getPlayerVideos(query: PlayerVideosQuery | number = {}): Observable<any> {
    const normalized = typeof query === 'number' ? { limit: query } : query;
    const params = buildHttpParams({
      page: normalized.page,
      limit: normalized.limit ?? 50,
      search: normalized.search,
      sort: normalized.sort,
      event_id: normalized.event_id,
      category_id: normalized.category_id,
      opponent: normalized.opponent,
      include_pending: normalized.include_pending
    });
    return this.unwrap(common_urls.get_player_videos, params);
  }

  getPlayerOpenEvents(): Observable<any> {
    return this.unwrap(common_urls.get_player_open_events);
  }

  /** Range mode for the overview year chart. Do not send `year` with these params. */
  getPlayerPerformanceRange(fromYear: number, toYear: number): Observable<any> {
    const params = new HttpParams()
      .set('from_year', fromYear.toString())
      .set('to_year', toYear.toString());
    return this.unwrap(common_urls.get_player_performance, params);
  }

  private unwrap(url: string, params?: HttpParams): Observable<any> {
    return this.httpClient.get<any>(url, { params, ...httpOptions }).pipe(
      shareReplay(),
      map((response: any) => {
        return {
          code: response.status,
          msg: response.body?.msg,
          data: response.body?.data
        } as any;
      })
    );
  }

  /** Cached so every consumer (dashboard layout, ranking trend, ...) shares one HTTP request. */
  private systemSettings$?: Observable<any>;

  getSystemSettings(): Observable<any> {
    if (!this.systemSettings$) {
      const url = environment.apiUrl + 'ott/v1/tenant/' + environment.tenant_Name + '/system-settings';
      this.systemSettings$ = this.httpClient.get<any>(url, httpOptions).pipe(
        map((response: any) => response.body),
        catchError((err) => {
          this.systemSettings$ = undefined;
          return throwError(() => err);
        }),
        shareReplay(1)
      );
    }
    return this.systemSettings$;
  }

  getTopFinishes(topFinish: number = 3): Observable<any> {
    const token = globalThis.window !== undefined && localStorage.getItem('token') ? localStorage.getItem('token') : '';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'auth': 'Bearer ' + token,
      'tenant': 'dev',
      'language': 'en'
    });
    let params = new HttpParams().set('top_finish', topFinish.toString());
    
    return this.httpClient.get<any>(environment.apiUrl + 'tms/v1/player/top-finish', { params, headers, observe: 'response' as const }).pipe(
      shareReplay(),
      map((response: any): HttpRes => {
        return {
          code: response.status,
          msg: response.body.msg,
          data: response.body.data
        } as any;
      })
    );
  }
}
